/**
 * Compatibility shim: next/image → native <img>
 *
 * Translates Next.js Image props to standard HTML img attributes.
 * Supports src (string | StaticImport), width, height, fill, priority, etc.
 *
 * When `priority` is set:
 *   - Injects `<link rel="preload" as="image">` into `<head>` (React 19 hoisting)
 *   - Sets `fetchpriority="high"` + `loading="eager"` on the `<img>`
 */
import type { AssetPath, LocalizedText } from '@letsgethome/types/branded';
import { forwardRef, type ImgHTMLAttributes } from 'react';

interface StaticImageData {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
}

// `src` is a STATIC asset only — a branded `AssetPath` (from `assetPath('/x')`)
// or an imported `StaticImageData`. A raw string and an `S3Key` are both
// type-rejected: the shim renders src verbatim (no imageserver URL), so dynamic
// S3 images must use `<S3Image src: S3Key>`. See the AssetPath brand docs.
type ImageSrc = AssetPath | StaticImageData;

/**
 * The alt contract: a meaningful localized alt, XOR an explicit `decorative`
 * marker (rendered as `alt=""`). The old `alt: LocalizedText | ''` blessed a
 * silent empty string, which made "forgot the alt" and "chose decorative"
 * indistinguishable — 49 content images shipped alt-less (Ahrefs wave 5).
 * Decorativeness must now be DECLARED, never defaulted, and `alt=""` at a
 * call site is a type error.
 */
export type ImageAltProps =
  | {
      /** Alt text — `t()`-translated or `plainText()`-data. A raw string
       * literal is a compile error, so hardcoded English can't reach a
       * screen reader. */
      alt: LocalizedText;
      decorative?: never;
    }
  | {
      alt?: never;
      /** Explicitly decorative — renders `alt=""` for screen readers. */
      decorative: true;
    };

interface ImageBaseProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'width' | 'height' | 'alt'
> {
  src: ImageSrc;
  width?: number | `${number}`;
  height?: number | `${number}`;
  fill?: boolean;
  priority?: boolean;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  loader?: (p: { src: string; width: number; quality?: number }) => string;
  sizes?: string;
  unoptimized?: boolean;
}

type ImageProps = ImageBaseProps & ImageAltProps;

const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  // `alt` is destructured (not left in `...rest`) so jsx-a11y can statically
  // verify the required prop reaches the rendered <img>.
  { src, alt, decorative, fill, priority, sizes, ...rest },
  ref
) {
  // Next.js <Image> compat props — accepted for drop-in API parity but
  // intentionally NOT forwarded to the native <img>. Stripped here in a nested
  // destructure (rather than the component params) to keep the prop surface
  // small; the `_`-prefix exempts them from no-unused-vars.
  const {
    quality: _quality,
    placeholder: _placeholder,
    blurDataURL: _blurDataURL,
    loader: _loader,
    unoptimized: _unoptimized,
    ...imgRest
  } = rest;

  const imgSrc = typeof src === 'string' ? src : src.src;

  const style: React.CSSProperties = fill
    ? {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        ...rest.style,
      }
    : (rest.style ?? {});

  return (
    <>
      {priority && (
        <link rel="preload" as="image" href={imgSrc} imageSizes={sizes} fetchPriority="high" />
      )}
      <img
        ref={ref}
        {...imgRest}
        alt={decorative ? '' : alt}
        aria-hidden={decorative ? true : undefined}
        src={imgSrc}
        sizes={sizes}
        style={style}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : undefined}
      />
    </>
  );
});

export default Image;
export type { ImageProps, StaticImageData };
